import { apiRoutes } from '@/constants/api-routes';
import { apiRequest, type QueryParams } from './http';
import type {
  ArticleLevel,
  ArticleStatus,
  ArticleTopic,
  ArticleView,
  PaginatedArticles,
} from '@/types/api';

/**
 * Query params accepted by `GET /articles` and `GET /admin/articles`.
 * `status` is only honoured by the admin endpoint — the public one
 * always scopes to `status = 'live'` server-side.
 */
export interface ListArticlesFilters {
  topic?: ArticleTopic[] | string[];
  level?: ArticleLevel[] | string[];
  status?: ArticleStatus;
  q?: string;
  limit?: number;
  offset?: number;
}

function toQueryParams(filters: ListArticlesFilters): QueryParams {
  const query: QueryParams = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    query[key] = value as QueryParams[string];
  }
  return query;
}

/**
 * Public article API — reads only. Mutations are admin-only and live
 * on `admin-articles.service.ts` so feature code doesn't accidentally
 * import a destructive action from a read-path module.
 */
export const articlesService = {
  async list(filters: ListArticlesFilters = {}): Promise<PaginatedArticles> {
    return apiRequest<PaginatedArticles>(apiRoutes.articles.list, {
      method: 'GET',
      query: toQueryParams(filters),
    });
  },

  async featured(): Promise<ArticleView | null> {
    return apiRequest<ArticleView | null>(apiRoutes.articles.featured, { method: 'GET' });
  },

  async getBySlug(slug: string): Promise<ArticleView> {
    return apiRequest<ArticleView>(apiRoutes.articles.bySlug(slug), { method: 'GET' });
  },
};
